import { lazy, Suspense } from 'react';

import { cn } from '../lib/cn.ts';
import { Field, type FieldPart, fieldBoxClass, splitField, useFieldState } from './field.tsx';
import type { RichTextInputProps } from './rich-text-doc.ts';

export {
  normalizeLink,
  type RichTextInputProps,
  type RichTextInsertions,
  richTextToPlain,
  type RichTextValue,
} from './rich-text-doc.ts';

/**
 * Редактор (Tiptap и ProseMirror — сотни килобайт) грузится, когда поле впервые показали: оболочка и экраны
 * без описаний его не тянут.
 */
const Editor = lazy(() =>
  import('./rich-text-editor.tsx').then((module) => ({ default: module.RichTextEditor })),
);

/** Пока редактор грузится — рамка той же высоты с полосой инструментов: поле не прыгает, когда он появится. */
function EditorPlaceholder({
  minHeight = 120,
  className,
  ...state
}: Pick<RichTextInputProps, 'minHeight' | 'className' | 'disabled' | 'readOnly' | 'invalid'>) {
  const field = useFieldState();
  const disabled = state.disabled ?? field?.disabled ?? false;
  const readOnly = state.readOnly ?? field?.readOnly ?? false;
  const invalid = state.invalid ?? field?.invalid ?? false;
  return (
    <div
      aria-hidden="true"
      className={fieldBoxClass({
        multiline: true,
        invalid,
        disabled,
        readOnly,
        className: cn('flex-col items-stretch gap-0 px-0 py-0', className),
      })}
    >
      {disabled || readOnly ? null : <div className="h-9 border-b border-border-soft" />}
      <div style={{ minHeight }} />
    </div>
  );
}

/**
 * Rich text: заголовки, списки, ссылки, жирный и курсив. Сочетания клавиш — как в текстовых
 * редакторах (⌘B, ⌘I, ⌘Z); ссылки из вставленного текста распознаются сами.
 */
export function RichTextInput(props: RichTextInputProps) {
  return (
    <Suspense fallback={<EditorPlaceholder {...props} />}>
      <Editor {...props} />
    </Suspense>
  );
}

export interface RichTextFieldProps extends FieldPart, Omit<RichTextInputProps, keyof FieldPart> {}

export function RichTextField(props: RichTextFieldProps) {
  const [field, input] = splitField(props);
  return (
    <Field {...field}>
      <RichTextInput {...input} />
    </Field>
  );
}
